/**
 * IntakeResponsePanel
 *
 * Cockpit "Intake" tab. Shows the pre-visit intake bound to this appointment:
 *   - none → empty state with "Send intake now" affordance
 *   - pending/started → status, sent timestamp, resend
 *   - completed → full response rendered inline
 *
 * Hooks into:
 *   - useEncounter() for appointment_id
 *   - GET /intakes/by-appointment/:appointmentId
 *   - POST /intakes/:id/resend
 *   - POST /intakes/responses/:responseId/review
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, FileText, AlertCircle, RefreshCw, Copy, ImageOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useEncounter } from '@/contexts/EncounterContext';
import { intakeService } from '@/services/intakeService';
import type { IntakeSection, IntakeQuestion } from '@/lib/intake/templates/types';
import { api } from '@/services/api';
import { getIntakeTemplate, therapyCategoryToTemplateType } from '@/lib/intake/templates';

interface PendingIntakeRich {
  id: string;
  status: 'pending' | 'started' | 'completed' | 'expired' | 'cancelled';
  intake_type: string;
  therapy_category: string;
  intake_form_template_id?: string | null;
  appointment_id: string;
  patient_email?: string | null;
  intake_link_token?: string | null;
  created_at: string;
  completed_at?: string | null;
  questionnaireResponse?: {
    id: string;
    responses: Record<string, any>;
    completed_at: string | null;
    verified_at: string | null;
    verified_by_practitioner_id: string | null;
    verified_by_user_id: string | null;
  } | null;
  intakeFormTemplate?: {
    id: string;
    name: string;
    description: string | null;
    schema: { sections: IntakeSection[] };
  } | null;
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  started: 'In progress',
  completed: 'Completed',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  started: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  completed: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  expired: 'bg-slate-200 text-slate-700 hover:bg-slate-200',
  cancelled: 'bg-slate-200 text-slate-700 hover:bg-slate-200',
};

const formatAnswer = (q: IntakeQuestion | null, value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map(v => formatAnswer(null, v)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return '—';
    // Symptom-checklist shape: { hot_flashes: true, night_sweats: false } → list truthy keys
    if (entries.every(([, v]) => typeof v === 'boolean')) {
      const truthy = entries.filter(([, v]) => v).map(([k]) => humanizeKey(k));
      return truthy.length === 0 ? '—' : truthy.join(', ');
    }
    // Mixed shape: { severity: 'moderate', frequency: 'daily' } → key: value pairs
    return entries.map(([k, v]) => `${humanizeKey(k)}: ${formatAnswer(null, v)}`).join(', ');
  }
  return String(value);
};

// Built-in TRT intake stores STOP-Bang answers under nested keys like
// `questionnaires.stopBang.snore`, but the template question IDs are
// `stopbang_snore`. Map the leaf names to the template flavor so the fuzzy
// matcher can bridge them.
const STOP_BANG_KEY_MAP: Record<string, string> = {
  snore: 'stopbang_snore',
  tired: 'stopbang_tired',
  observed_apneas: 'stopbang_observed',
  high_blood_pressure: 'stopbang_pressure',
  bmi_over_35: 'stopbang_bmi',
  age_over_50: 'stopbang_age',
  neck_over_40cm: 'stopbang_neck',
  male_gender: 'stopbang_male',
};

const toSnakeCase = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/\./g, '_')
    .toLowerCase();

// Generate the universe of question-id spellings a single canonical id can
// appear under in a stored response payload — covers dotted vs underscored,
// camelCase vs snake_case, with/without the legacy `questionnaires.` prefix.
// Mirrors the variants used in IntakeResponseReviewDrawer so the two surfaces
// label the same fields the same way.
const normalizeQuestionIdCandidates = (questionId: string): string[] => {
  const candidates = new Set<string>();
  const raw = questionId || '';
  const stripped = raw.replace(/^questionnaires\./, '');
  const lastPart = raw.split('.').pop() || raw;
  candidates.add(stripped);
  candidates.add(stripped.replace(/\./g, '_'));
  candidates.add(lastPart);
  candidates.add(toSnakeCase(lastPart));
  candidates.add(toSnakeCase(stripped));
  candidates.add(toSnakeCase(raw));
  const [group, ...restParts] = stripped.split('.');
  const rest = restParts.join('.');
  const groupLower = group?.toLowerCase();
  if (groupLower === 'adam' && rest) {
    candidates.add(`adam_${rest}`);
    candidates.add(`adam_${rest.replace(/\./g, '_')}`);
  }
  if (groupLower === 'stopbang' && rest) {
    const restLower = rest.toLowerCase();
    const mapped = STOP_BANG_KEY_MAP[restLower];
    if (mapped) candidates.add(mapped);
    candidates.add(`stopbang_${restLower}`);
  }
  return Array.from(candidates).map(c => c.toLowerCase());
};

interface TemplateQuestionInfo {
  questionId: string;
  text: string;
  sectionTitle: string;
  sectionIndex: number;
  questionIndex: number;
}

const buildTemplateLookup = (
  sections: IntakeSection[] | null,
): Map<string, TemplateQuestionInfo> | null => {
  if (!sections) return null;
  const lookup = new Map<string, TemplateQuestionInfo>();
  sections.forEach((section, sectionIndex) => {
    section.questions.forEach((q, questionIndex) => {
      lookup.set(q.id.toLowerCase(), {
        questionId: q.id,
        text: q.text,
        sectionTitle: section.title,
        sectionIndex,
        questionIndex,
      });
    });
  });
  return lookup;
};

const findTemplateInfo = (
  questionId: string,
  lookup: Map<string, TemplateQuestionInfo> | null,
): TemplateQuestionInfo | null => {
  if (!lookup) return null;
  for (const candidate of normalizeQuestionIdCandidates(questionId)) {
    const info = lookup.get(candidate);
    if (info) return info;
  }
  return null;
};

// Address/file leaf shapes from the intake renderer. Treat them as scalar
// values rather than recursing into their fields.
const isFileUploadShape = (v: unknown): boolean =>
  !!v && typeof v === 'object' && !Array.isArray(v) && 's3Key' in (v as object);
const isAddressShape = (v: unknown): boolean =>
  !!v && typeof v === 'object' && !Array.isArray(v) && 'address_line_1' in (v as object);

// Walk a response object into a flat `{ questionId, value }` list. Nested
// plain objects expand into dotted keys (e.g. `questionnaires.adam.q1...`)
// so the fuzzy matcher can find them under any template-id flavor.
interface FlatAnswer { questionId: string; value: unknown }
const flattenResponseObject = (responses: any): FlatAnswer[] => {
  const out: FlatAnswer[] = [];
  const walk = (obj: any, prefix: string) => {
    for (const [key, value] of Object.entries(obj || {})) {
      if (key.startsWith('consent')) continue;
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const isPlainObject = value !== null
        && typeof value === 'object'
        && !Array.isArray(value)
        && !isFileUploadShape(value)
        && !isAddressShape(value);
      if (isPlainObject && Object.keys(value as object).length > 0) {
        walk(value, fullKey);
      } else {
        out.push({ questionId: fullKey, value });
      }
    }
  };
  walk(responses, '');
  return out;
};

// Anything stored in S3 by an intake upload lives under `public-intake/`
// and ends with a recognisable image extension. Same detection the
// IntakeResponseReviewDrawer uses, so the two surfaces stay aligned.
const S3_IMAGE_RE = /^public-intake\/.+\.(jpe?g|png|gif|webp)$/i;
const isS3ImageKey = (v: unknown): v is string =>
  typeof v === 'string' && S3_IMAGE_RE.test(v);

const VIEW_URL_BASE = (
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
).replace(/\/+$/, '');

// Fetches a short-lived presigned GET URL for an S3 key. Cancels on
// unmount so a stale response doesn't set state on a dead component.
function useS3ViewUrl(key: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    setUrl(null);
    setError(null);
    fetch(`${VIEW_URL_BASE}/uploads/view-url?key=${encodeURIComponent(key)}`)
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        if (j?.success && j?.data?.viewUrl) setUrl(j.data.viewUrl);
        else setError(j?.message || 'Failed to load image');
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'Failed to load image');
      });
    return () => {
      cancelled = true;
    };
  }, [key]);
  return { url, error };
}

const S3ImagePreview: React.FC<{ s3Key: string }> = ({ s3Key }) => {
  const { url, error } = useS3ViewUrl(s3Key);
  const [open, setOpen] = useState(false);

  if (error) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-500" title={error}>
        <ImageOff className="h-3.5 w-3.5" /> Image unavailable
      </span>
    );
  }
  if (!url) {
    return <div className="h-20 w-28 animate-pulse rounded border border-slate-200 bg-slate-100" />;
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block cursor-zoom-in rounded border border-slate-200 overflow-hidden bg-slate-50 hover:border-slate-300"
        aria-label="Open image full screen"
      >
        <img
          src={url}
          alt="Intake upload"
          className="h-20 w-auto max-w-[160px] object-cover"
        />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl border-slate-800 bg-slate-900 p-2">
          <img
            src={url}
            alt="Intake upload — full size"
            className="mx-auto max-h-[85vh] w-auto rounded"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Wrapper that decides whether to render an image preview or plain text
// for a single response cell. Centralises the s3-image detection so all
// three render paths (stages / flat-with-template / raw-fallback) get
// the same behaviour.
const AnswerCell: React.FC<{ rawValue: unknown; fallbackText: string }> = ({ rawValue, fallbackText }) => {
  if (isS3ImageKey(rawValue)) {
    return <S3ImagePreview s3Key={rawValue} />;
  }
  if (Array.isArray(rawValue) && rawValue.some(isS3ImageKey)) {
    return (
      <div className="flex flex-wrap gap-2">
        {rawValue.map((v, i) =>
          isS3ImageKey(v)
            ? <S3ImagePreview key={i} s3Key={v} />
            : <span key={i} className="text-sm text-slate-900">{String(v)}</span>,
        )}
      </div>
    );
  }
  return <span className="text-sm text-slate-900">{fallbackText}</span>;
};

// Pulls the raw scalar (or array) out of a stages-format response so
// the rendering layer can inspect it before deciding image vs. text.
const extractStageRaw = (response: any): unknown => {
  if (!response || response.answered === false) return null;
  if (response.response_text !== null && response.response_text !== undefined && response.response_text !== '') {
    return response.response_text;
  }
  if (response.response_number !== null && response.response_number !== undefined) {
    return response.response_number;
  }
  if (Array.isArray(response.selected_options) && response.selected_options.length > 0) {
    return response.selected_options.map((o: any) => o.text || o.id);
  }
  return null;
};

const IntakeResponsePanel: React.FC = () => {
  const { encounter, appointment } = useEncounter();
  const appointmentId = appointment?.id || (encounter as any)?.appointment_id || null;
  const { toast } = useToast();

  const [intakes, setIntakes] = useState<PendingIntakeRich[]>([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!appointmentId) {
      setIntakes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await intakeService.getIntakesForAppointment(appointmentId);
      setIntakes((res.data || []) as PendingIntakeRich[]);
    } catch (err: any) {
      toast({ title: 'Failed to load intake', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [appointmentId, toast]);

  useEffect(() => { reload(); }, [reload]);

  // Mark completed responses as reviewed when the panel opens
  useEffect(() => {
    intakes.forEach(intake => {
      if (intake.status === 'completed'
          && intake.questionnaireResponse
          && !intake.questionnaireResponse.verified_at) {
        api.post(`/intakes/responses/${intake.questionnaireResponse.id}/review`, {})
          .catch(() => { /* best effort */ });
      }
    });
  }, [intakes]);

  const handleResend = async (intakeId: string) => {
    setResending(intakeId);
    try {
      await api.post(`/intakes/${intakeId}/resend`, {});
      toast({ title: 'Intake email resent' });
    } catch (err: any) {
      toast({ title: 'Resend failed', description: err.message, variant: 'destructive' });
    } finally {
      setResending(null);
    }
  };

  const handleCopyLink = (intake: PendingIntakeRich) => {
    if (!intake.intake_link_token) return;
    const baseUrl = window.location.origin;
    const path = intake.intake_form_template_id
      ? `/patient/intake/custom/${intake.intake_link_token}`
      : intake.therapy_category === 'trt' ? `/patient/intake/mens/${intake.intake_link_token}`
      : intake.therapy_category === 'hrt' ? `/patient/intake/womens/${intake.intake_link_token}`
      : intake.therapy_category === 'glp1' ? `/patient/intake/weightloss/${intake.intake_link_token}`
      : `/patient/intake/${intake.intake_link_token}`;
    navigator.clipboard.writeText(`${baseUrl}${path}`);
    toast({ title: 'Intake link copied' });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading intake…
      </div>
    );
  }

  if (!appointmentId) {
    return (
      <EmptyState message="No appointment is linked to this encounter — intake context is not available." />
    );
  }

  if (intakes.length === 0) {
    return (
      <EmptyState
        message="No pre-visit intake on file for this appointment."
        appointmentId={appointmentId}
      />
    );
  }

  return (
    <div className="space-y-6">
      {intakes.map(intake => (
        <IntakeCard
          key={intake.id}
          intake={intake}
          onResend={() => handleResend(intake.id)}
          onCopyLink={() => handleCopyLink(intake)}
          resending={resending === intake.id}
        />
      ))}
    </div>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const EmptyState: React.FC<{ message: string; appointmentId?: string }> = ({ message }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center">
    <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
    <p className="text-sm text-slate-700 font-medium">{message}</p>
    <p className="text-xs text-slate-500 mt-1">
      Send one from the patient profile or attach a default to this appointment type.
    </p>
  </div>
);

const IntakeCard: React.FC<{
  intake: PendingIntakeRich;
  onResend: () => void;
  onCopyLink: () => void;
  resending: boolean;
}> = ({ intake, onResend, onCopyLink, resending }) => {
  const isComplete = intake.status === 'completed' && !!intake.questionnaireResponse;
  const formName = intake.intakeFormTemplate?.name
    || `${(intake.therapy_category || 'general').toUpperCase()} intake`;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-slate-900">{formName}</h3>
            <Badge className={STATUS_BADGE_CLASS[intake.status] || ''}>
              {STATUS_LABEL[intake.status] || intake.status}
            </Badge>
            {intake.intakeFormTemplate ? (
              <Badge variant="outline" className="text-xs">Custom form</Badge>
            ) : (
              <Badge variant="outline" className="text-xs">System form</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Sent {new Date(intake.created_at).toLocaleString()}
            {isComplete && intake.completed_at
              ? ` · Completed ${new Date(intake.completed_at).toLocaleString()}`
              : ''}
          </p>
        </div>
        {!isComplete && intake.status !== 'expired' && intake.status !== 'cancelled' && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCopyLink}>
              <Copy className="h-3 w-3 mr-1" /> Copy link
            </Button>
            <Button variant="outline" size="sm" onClick={onResend} disabled={resending}>
              {resending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Resend
            </Button>
          </div>
        )}
      </div>

      {!isComplete ? (
        <div className="rounded-md bg-slate-50 p-4 text-sm text-slate-600 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <span>
            Patient hasn't completed this intake yet. The visit can still proceed —
            this is just a soft warning so you know the pre-visit context is missing.
          </span>
        </div>
      ) : (
        <ResponseSections intake={intake} />
      )}
    </div>
  );
};

/**
 * Pull the human-facing answer string out of a stages-format response object.
 * Mirrors the persistence mapping in
 * novamed-api/src/services/intakeService.js → buildStagesPayloadFromCustomTemplate.
 */
const formatStageAnswer = (response: any): string => {
  if (!response || response.answered === false) return '—';
  if (response.response_text !== null && response.response_text !== undefined && response.response_text !== '') {
    return String(response.response_text);
  }
  if (response.response_number !== null && response.response_number !== undefined) {
    return String(response.response_number);
  }
  if (Array.isArray(response.selected_options) && response.selected_options.length > 0) {
    return response.selected_options.map((o: any) => o.text || o.id).join(', ');
  }
  return '—';
};

const ResponseSections: React.FC<{ intake: PendingIntakeRich }> = ({ intake }) => {
  const responses: any = intake.questionnaireResponse?.responses || {};

  // New canonical shape: stages payload built server-side at submit time.
  // The stages tree already carries section + question text, so render
  // directly from it without needing the template lookup.
  if (responses && Array.isArray(responses.stages) && responses.stages.length > 0) {
    return (
      <div className="space-y-5">
        {responses.stages.map((stage: any) => {
          const questions = stage?.questionnaire?.questions || [];
          if (questions.length === 0) return null;
          return (
            <div key={stage.stage_id || stage.stage_name} className="space-y-3">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-medium">
                {stage.stage_name}
              </h4>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {questions.map((q: any) => {
                  const raw = extractStageRaw(q.response);
                  return (
                    <div key={q.question_id} className="space-y-0.5">
                      <dt className="text-xs text-slate-500">{q.text}</dt>
                      <dd>
                        <AnswerCell rawValue={raw} fallbackText={formatStageAnswer(q.response)} />
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          );
        })}
      </div>
    );
  }

  // Flat format. Built-in therapy intakes (TRT / HRT / GLP-1) submit a flat
  // camelCase blob with nested `questionnaires.*` groups, while template
  // question IDs are snake_case. Recursively flatten the payload, then
  // fuzzy-match each key against the resolved template so every answered
  // question shows up — not just the ones whose key happens to equal the
  // template ID verbatim. Same matching strategy as the review drawer.
  const sections = resolveTemplateSections(intake);
  const templateLookup = buildTemplateLookup(sections);
  const flat = flattenResponseObject(responses);

  interface AnswerRow {
    questionId: string;
    value: unknown;
    text: string;
    sectionTitle: string;
    sectionIndex: number;
    questionIndex: number;
  }
  const OTHER_BUCKET = '__other__';
  const groups: Record<string, AnswerRow[]> = {};
  let otherCounter = 0;
  for (const { questionId, value } of flat) {
    if (value === undefined) continue;
    const info = findTemplateInfo(questionId, templateLookup);
    const row: AnswerRow = info
      ? {
          questionId,
          value,
          text: info.text,
          sectionTitle: info.sectionTitle,
          sectionIndex: info.sectionIndex,
          questionIndex: info.questionIndex,
        }
      : {
          questionId,
          value,
          text: humanizeKey(questionId),
          sectionTitle: OTHER_BUCKET,
          sectionIndex: 9999,
          questionIndex: otherCounter++,
        };
    const bucket = info ? info.sectionTitle : OTHER_BUCKET;
    if (!groups[bucket]) groups[bucket] = [];
    groups[bucket].push(row);
  }

  const orderedSectionKeys = Object.keys(groups).sort((a, b) => {
    if (a === OTHER_BUCKET) return 1;
    if (b === OTHER_BUCKET) return -1;
    return (groups[a][0]?.sectionIndex ?? 999) - (groups[b][0]?.sectionIndex ?? 999);
  });
  for (const key of orderedSectionKeys) {
    groups[key].sort((x, y) => x.questionIndex - y.questionIndex);
  }

  if (orderedSectionKeys.length === 0) {
    return <p className="text-sm text-slate-500">No response data on file.</p>;
  }

  return (
    <div className="space-y-5">
      {orderedSectionKeys.map(key => (
        <div key={key} className="space-y-3">
          <h4 className="text-xs uppercase tracking-wide text-slate-500 font-medium">
            {key === OTHER_BUCKET ? 'Other' : key}
          </h4>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {groups[key].map((row, i) => (
              <div key={`${row.questionId}-${i}`} className="space-y-0.5">
                <dt className="text-xs text-slate-500">{row.text}</dt>
                <dd>
                  <AnswerCell rawValue={row.value} fallbackText={formatAnswer(null, row.value)} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
};

/**
 * Pick a section list for label resolution. Prefers the saved custom-form
 * schema; falls back to the built-in therapy template (the same chain used
 * by IntakeResponseReviewDrawer's templateLookup).
 */
function resolveTemplateSections(intake: PendingIntakeRich): IntakeSection[] | null {
  const customSections = intake.intakeFormTemplate?.schema?.sections;
  if (customSections && customSections.length > 0) return customSections;

  const cat = intake.therapy_category;
  if (!cat) return null;
  try {
    const templateType = therapyCategoryToTemplateType(cat);
    const template = getIntakeTemplate(templateType);
    return (template?.sections as IntakeSection[]) || null;
  } catch {
    return null;
  }
}

const humanizeKey = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());

export default IntakeResponsePanel;
